import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { Box } from "@mui/material";

import ForgotPassword from "./Components/Auth/ForgotPassword";
import ResetPassword from "./Components/Auth/ResetPassword";
import SetPassword from "./Components/Auth/SetPassword";
import SignIn from "./Components/Auth/SignIn";
import AppLayout from "./Components/Layout/AppLayout";
import {
  UnAuthenticatedRoute,
  AuthenticatedRoute,
  PlatformAuthenticatedRoute,
} from "./utils/Route";

import Studies from "./Components/Studies/Studies";
// import Survey from "./Components/Survey/Survey";
import ChangePassword from "./Components/Auth/ChangePassword";
import Error404 from "./Components/Layout/404";
// import CustomSignIn from "./Components/Auth/CustomSignIn";
// import WelcomePage from "./Components/Survey/WelcomePage";
// import ThankYouPage from "./Components/Survey/ThankYouPage";
import SurveyWrapper from "./Components/Survey/SurveyWrapper";
import ThankYouWrapper from "./Components/Survey/ThankYouWrapper";
import QuestionPrintPhase from "./Components/QuestionBuilder/QuestionPrint/QuestionPrintPhase";
import QuestionPrintForm from "./Components/QuestionBuilder/QuestionPrint/QuestionPrintForm";
import Organisations from "./Components/Organisations/Organisations";
import CreateOrganisation from "./Components/Organisations/CreateOrganisation";
import ViewOrganisation from "./Components/Organisations/ViewOrganisation/ViewOrganisation";
import Features from "./Components/Features/Features";
import OrganisationSettings from "./Components/OrganisationAdmin/Settings/Settings";
import OrganisationUsers from "./Components/OrganisationAdmin/Users/Users";
import OrganisationUserDetails from "./Components/OrganisationAdmin/Users/OrganisationUserDetails";
import OTPVerification from "./Components/Auth/OTPVerification";
// import UserRegistration from "./Components/Auth/UserRegistration";
import PlatformUsers from "./Components/Platform/Users/Users";
import { platformAdminPermissions } from "./utils/roles";
import PrintResponse from "./Components/Survey/PrintResponse/PrintResponse";
import Instruments from "./Components/Platform/Instruments/Instruments";
import AllStudies from "./Components/Studies/AllStudies";
import PlatformConfigTabs from "./Components/PlatformFeatures/PlatformConfigTabs";
// import { ParticipantJoinCode } from "./Routes/ParticipantsRoutes";
import Home from "./Home";
import LinkParticipant from "./LinkParticipant";
// import WebviewWrapper from "./Components/CMS/Webview/WebviewWrapper";
// import LandingPage from "./Components/LandingPage/LandingPage";
import LandingPageV2 from "./Components/LandingPage/LandingPageV2";
import Screening from "./Components/Screening/Screening";
import ThankYou from "./Components/Screening/ThankYou";
import Participants from "./Components/PlatformParticipant/Participants";
import GlobalParticipantTabs from "./Components/PlatformParticipant/GlobalParticipantTabs";
import PlatformDashboard from "./Components/Dashboard/PlatformDashboard/PlatformDashboard";
import AllSites from "./Components/Sites/AllSites";
// import PatientRegistrationV2 from "./Components/Auth/PatientRegistrationV2";
// import ParticipantLoginV2 from "./Components/Auth/ParticipantLoginV2";
// import VerifyRegistration from "./Components/Auth/VerifyRegistration";
// import AccountValidationSuccessful from "./Components/Auth/AccountValidationSuccessful";
// import { Navbar } from "./Components/Common/UI/Navbar";
import StudyConsents from "./Components/ParticipantConsent/StudyConsents";
import StudySurvey from "./Components/Survey/ParticipantSurvey/StudySurvey";
import CoreSurvey from "./Components/Survey/ParticipantSurvey/CoreSurvey";
import MyHealthRecords from "./Components/Survey/ParticipantSurvey/MyHealthRecords";
import CoreConsents from "./Components/ParticipantConsent/CoreConsents";
import Survey from "./Components/Survey/ParticipantSurvey/Survey";
import ParticipantLayout from "./Components/Layout/ParticipantLayout";
import ParticipantLogin from "./Components/Auth/ParticipantLogin";
import AccountCreation from "./Components/Auth/AccountCreation";
import EmailVerification from "./Components/Auth/EmailVerification";
import ConsentForm from "./Components/ParticipantConsent/Sign/ConsentForm/ConsentForm";

const routes = createBrowserRouter([
  {
    path: "/auth",
    element: (
      <UnAuthenticatedRoute>
        <Outlet />
      </UnAuthenticatedRoute>
    ),
    children: [
      {
        path: "login",
        element: <SignIn />,
      },
      // {
      //   path: "login/:slug",
      //   element: <CustomSignIn />,
      // },
      // {
      //   path: "register/:slug",
      //   element: <UserRegistration />,
      // },
      {
        path: "forgot-password",
        element: <ForgotPassword />,
      },
      {
        path: "reset-password/:token",
        element: <ResetPassword />,
      },
      {
        path: "signup",
        element: <SetPassword />,
      },
      {
        path: "2fa",
        element: <OTPVerification />,
      },
      // {
      //   path: "participant/login",
      //   element: <ParticipantJoinCode />,
      // },
    ],
  },
  {
    path: "/profile/change-password",
    element: <ChangePassword />,
  },
  {
    path: "/home",
    element: <Home />,
  },
  {
    path: "/app",
    element: (
      <AuthenticatedRoute>
        <AppLayout />
      </AuthenticatedRoute>
    ),
    children: [
      {
        path: "dashboard",
        element: <PlatformDashboard />,
      },
      {
        path: "studies",
        element: <Studies />,
      },
      {
        path: "participants",
        children: [
          {
            index: true,
            element: <Participants />,
          },
          {
            path: ":participantId",
            element: <GlobalParticipantTabs />,
          },
        ],
      },
      {
        path: "organisation",
        children: [
          {
            index: true,
            element: <Organisations />,
          },
          {
            path: "builder/:id",
            element: <CreateOrganisation />,
          },
          {
            path: "view/:id",
            element: <ViewOrganisation />,
          },
        ],
      },
      {
        path: "features",
        element: (
          <PlatformAuthenticatedRoute
            action={platformAdminPermissions.managePlatform}
          >
            <Features />
          </PlatformAuthenticatedRoute>
        ),
      },
      {
        path: "settings/:id",
        element: <OrganisationSettings />,
      },
      {
        path: "users",
        children: [
          {
            index: true,
            element: <OrganisationUsers />,
          },
          {
            path: "details/:id",
            element: <OrganisationUserDetails />,
          },
        ],
      },
      {
        path: "platform-users",
        element: (
          <PlatformAuthenticatedRoute
            action={platformAdminPermissions.manageUser}
          >
            <PlatformUsers />
          </PlatformAuthenticatedRoute>
        ),
      },
      {
        path: "all-studies",
        children: [
          {
            index: true,
            element: (
              <PlatformAuthenticatedRoute
                action={platformAdminPermissions.createStudy}
              >
                <AllStudies />
              </PlatformAuthenticatedRoute>
            ),
          },
          {
            path: "study/:id",
            element: (
              <PlatformAuthenticatedRoute
                action={platformAdminPermissions.createStudy}
              >
                <PlatformConfigTabs />
              </PlatformAuthenticatedRoute>
            ),
          },
        ],
      },

      {
        path: "templates",
        element: (
          <PlatformAuthenticatedRoute
          // action={platformAdminPermissions.viewPlatform}
          >
            <Instruments />
          </PlatformAuthenticatedRoute>
        ),
      },
      {
        path: "sites",
        element: (
          <PlatformAuthenticatedRoute>
            <AllSites />
          </PlatformAuthenticatedRoute>
        ),
      },
    ],
  },
  {
    path: "surveys/:surveySlug",
    element: <SurveyWrapper />,
  },
  {
    path: "surveys/:surveySlug/thank-you",
    element: <ThankYouWrapper />,
  },
  {
    path: "404",
    element: <Error404 />,
  },
  {
    path: "thank-you",
    element: <ThankYou />,
  },
  {
    path: "print/study/:id/phase/:type",
    element: (
      <AuthenticatedRoute>
        <QuestionPrintPhase />
      </AuthenticatedRoute>
    ),
  },
  {
    path: "print/study/:id/form/:formId",
    element: (
      <AuthenticatedRoute>
        <QuestionPrintForm />
      </AuthenticatedRoute>
    ),
  },
  {
    path: "print/response",
    element: <PrintResponse />,
  },
  {
    path: "/studies/:id",
    lazy: async () => {
      const { StudyLayoutComp } = await import("./Routes/StudyRoutes");
      return { Component: StudyLayoutComp };
    },
    children: [
      {
        path: "dashboard",
        lazy: async () => {
          const { DashboardComp } = await import("./Routes/StudyRoutes");
          return { Component: DashboardComp };
        },
      },
      {
        path: "study-designer",
        children: [
          {
            index: true,
            lazy: async () => {
              const { StudyDesignerComp } = await import(
                "./Routes/StudyRoutes"
              );
              return { Component: StudyDesignerComp };
            },
          },
          {
            path: "form/:formId/edit",
            lazy: async () => {
              const { QuestionBuilderComp } = await import(
                "./Routes/StudyRoutes"
              );
              return { Component: QuestionBuilderComp };
            },
          },
          {
            path: "form/:formId/edit-translation",
            lazy: async () => {
              const { QuestionBuilderTranslationComp } = await import(
                "./Routes/StudyRoutes"
              );
              return { Component: QuestionBuilderTranslationComp };
            },
          },
          {
            path: "form/:formId/preview",
            lazy: async () => {
              const { QuestionPreviewComp } = await import(
                "./Routes/StudyRoutes"
              );
              return { Component: QuestionPreviewComp };
            },
          },
        ],
      },
      {
        path: "participants",
        children: [
          {
            index: true,
            lazy: async () => {
              const { RecordsComp } = await import("./Routes/StudyRoutes");
              return { Component: RecordsComp };
            },
          },
          {
            path: ":participantId",
            children: [
              {
                path: "details",
                lazy: async () => {
                  const { ParticipantDetailsComp } = await import(
                    "./Routes/StudyRoutes"
                  );
                  return { Component: ParticipantDetailsComp };
                },
              },
              // {
              //   path: "timeline",
              //   lazy: async () => {
              //     const { ParticipantTimelineComp } = await import(
              //       "./Routes/StudyRoutes"
              //     );
              //     return { Component: ParticipantTimelineComp };
              //   },
              // },
              {
                path: "consent-form/:consentId",
                lazy: async () => {
                  const { ConsentFormComp } = await import(
                    "./Routes/StudyRoutes"
                  );
                  return { Component: ConsentFormComp };
                },
              },
              {
                path: "responses",
                lazy: async () => {
                  const { ResponsesComp } = await import(
                    "./Routes/StudyRoutes"
                  );
                  return { Component: ResponsesComp };
                },
              },
              {
                path: "responses/:formId/compare",
                lazy: async () => {
                  const { CompareResponsesComp } = await import(
                    "./Routes/StudyRoutes"
                  );
                  return { Component: CompareResponsesComp };
                },
              },
              {
                path: "analysis",
                lazy: async () => {
                  const { StatisticsComp } = await import(
                    "./Routes/StudyRoutes"
                  );
                  return { Component: StatisticsComp };
                },
              },
              {
                path: "prescription",
                lazy: async () => {
                  const { PrescriptionComp } = await import(
                    "./Routes/StudyRoutes"
                  );
                  return { Component: PrescriptionComp };
                },
              },
            ],
          },
        ],
      },
      {
        path: "participant-groups",
        lazy: async () => {
          const { ParticipantGroupsComp } = await import(
            "./Routes/StudyRoutes"
          );
          return { Component: ParticipantGroupsComp };
        },
      },
      {
        path: "e-consent",
        children: [
          {
            index: true,
            lazy: async () => {
              const { EConsentComp } = await import("./Routes/StudyRoutes");
              return { Component: EConsentComp };
            },
          },
          {
            path: "form/:formId/edit",
            lazy: async () => {
              const { ConsentBuilderComp } = await import(
                "./Routes/StudyRoutes"
              );
              return { Component: ConsentBuilderComp };
            },
          },

          {
            path: "form/:formId/preview",
            lazy: async () => {
              const { ConsentPreviewComp } = await import(
                "./Routes/StudyRoutes"
              );
              return { Component: ConsentPreviewComp };
            },
          },
          {
            path: "form/:formId/providerSign",
            lazy: async () => {
              const { ConsentPreviewComp } = await import(
                "./Routes/StudyRoutes"
              );
              return { Component: ConsentPreviewComp };
            },
          },
        ],
      },
      // {
      //   path: "medication",
      //   lazy: async () => {
      //     const { MedicationComp } = await import("./Routes/StudyRoutes");
      //     return { Component: MedicationComp };
      //   },
      // },
      {
        path: "audit-trail",
        lazy: async () => {
          const { AuditTrailComp } = await import("./Routes/StudyRoutes");
          return { Component: AuditTrailComp };
        },
      },
      {
        path: "export",
        lazy: async () => {
          const { ExportComp } = await import("./Routes/StudyRoutes");
          return { Component: ExportComp };
        },
      },
      {
        path: "econsents-tracking",
        lazy: async () => {
          const { ConsentsTrackingComp } = await import("./Routes/StudyRoutes");
          return { Component: ConsentsTrackingComp };
        },
      },
      // {
      //   path: "medication",
      //   lazy: async () => {
      //     const { MedicationComp } = await import("./Routes/StudyRoutes");
      //     return { Component: MedicationComp };
      //   },
      // },
      {
        path: "study-settings",
        lazy: async () => {
          const { StudySettingsComp } = await import("./Routes/StudyRoutes");
          return { Component: StudySettingsComp };
        },
      },
      // {
      //   path: "study-theme",
      //   lazy: async () => {
      //     const { StudyThemePanelComp } = await import("./Routes/StudyRoutes");
      //     return { Component: StudyThemePanelComp };
      //   },
      // },
      {
        path: "users",
        children: [
          {
            index: true,
            lazy: async () => {
              const { UsersComp } = await import("./Routes/StudyRoutes");
              return { Component: UsersComp };
            },
          },
          {
            path: ":userId",
            lazy: async () => {
              const { UserDetailComp } = await import("./Routes/StudyRoutes");
              return { Component: UserDetailComp };
            },
          },
        ],
      },

      {
        path: "form-permissions",
        lazy: async () => {
          const { FormPermissionsComp } = await import("./Routes/StudyRoutes");
          return { Component: FormPermissionsComp };
        },
      },
      {
        path: "permissions",
        lazy: async () => {
          const { RolesListComp } = await import("./Routes/StudyRoutes");
          return { Component: RolesListComp };
        },
      },
      {
        path: "queries",
        lazy: async () => {
          const { QueriesComp } = await import("./Routes/StudyRoutes");
          return { Component: QueriesComp };
        },
      },
      {
        path: "automations",
        children: [
          {
            index: true,
            lazy: async () => {
              const { AutomationsComp } = await import("./Routes/StudyRoutes");
              return { Component: AutomationsComp };
            },
          },
          {
            path: "add",
            lazy: async () => {
              const { AddAutomationComp } = await import(
                "./Routes/StudyRoutes"
              );
              return { Component: AddAutomationComp };
            },
          },
        ],
      },
      // {
      //   path: "appointments",
      //   children: [
      //     {
      //       index: true,
      //       lazy: async () => {
      //         const { AppointmentsComp } = await import("./Routes/StudyRoutes");
      //         return { Component: AppointmentsComp };
      //       },
      //     },
      //     {
      //       path: "list",
      //       lazy: async () => {
      //         const { AppointmentListViewComp } = await import(
      //           "./Routes/StudyRoutes"
      //         );
      //         return { Component: AppointmentListViewComp };
      //       },
      //     },
      //   ],
      // },
      {
        path: "my-appointments",
        lazy: async () => {
          const { AppointmentsComp } = await import("./Routes/StudyRoutes");
          return { Component: AppointmentsComp };
        },
      },
      {
        path: "team-calendar",
        lazy: async () => {
          const { AppointmentsComp } = await import("./Routes/StudyRoutes");
          return { Component: AppointmentsComp };
        },
      },
      // {
      //   path: "my-availability",
      //   lazy: async () => {
      //     const { MyAvailabilityComp } = await import("./Routes/StudyRoutes");
      //     return { Component: MyAvailabilityComp };
      //   },
      // },

      // {
      //   path: "messages",
      //   lazy: async () => {
      //     const { MessagesComp } = await import("./Routes/StudyRoutes");
      //     return { Component: MessagesComp };
      //   },
      // },
      // {
      //   path: "exercise",
      //   children: [
      //     {
      //       index: true,
      //       lazy: async () => {
      //         const { ExerciseCollectionComp } = await import(
      //           "./Routes/StudyRoutes"
      //         );
      //         return { Component: ExerciseCollectionComp };
      //       },
      //     },
      //     {
      //       path: ":exerciseId/categories",
      //       lazy: async () => {
      //         const { ExerciseCollectionDetailsComp } = await import(
      //           "./Routes/StudyRoutes"
      //         );
      //         return { Component: ExerciseCollectionDetailsComp };
      //       },
      //     },
      //     {
      //       path: ":exerciseId/categories/:categoryId/sections",
      //       lazy: async () => {
      //         const { SectionBuilderComp } = await import(
      //           "./Routes/StudyRoutes"
      //         );
      //         return { Component: SectionBuilderComp };
      //       },
      //     },
      //     {
      //       path: "builder",
      //       lazy: async () => {
      //         const { ExerciseBuilderComp } = await import(
      //           "./Routes/StudyRoutes"
      //         );
      //         return { Component: ExerciseBuilderComp };
      //       },
      //     },
      //   ],
      // },
      // {
      //   path: "cms",
      //   children: [
      //     {
      //       index: true,
      //       lazy: async () => {
      //         const { CMSComp } = await import("./Routes/StudyRoutes");
      //         return { Component: CMSComp };
      //       },
      //     },
      //     {
      //       path: "learning/:categoryId",
      //       lazy: async () => {
      //         const { LearningModuleDetailsComp } = await import(
      //           "./Routes/StudyRoutes"
      //         );
      //         return { Component: LearningModuleDetailsComp };
      //       },
      //     },
      //     {
      //       path: ":moduleId",
      //       lazy: async () => {
      //         const { UnitsComp } = await import("./Routes/StudyRoutes");
      //         return { Component: UnitsComp };
      //       },
      //     },
      //     {
      //       path: "units/:moduleId",
      //       lazy: async () => {
      //         const { PagesComp } = await import("./Routes/StudyRoutes");
      //         return { Component: PagesComp };
      //       },
      //     },
      //     {
      //       path: "review/:moduleId",
      //       lazy: async () => {
      //         const { ReviewsComp } = await import("./Routes/StudyRoutes");
      //         return { Component: ReviewsComp };
      //       },
      //     },
      //     {
      //       path: "builder/:unitId",
      //       lazy: async () => {
      //         const { CMSBuilderComp } = await import("./Routes/StudyRoutes");
      //         return { Component: CMSBuilderComp };
      //       },
      //     },
      //     {
      //       path: "preview/:unitId",
      //       lazy: async () => {
      //         const { CMSPreviewWrapperComp } = await import(
      //           "./Routes/StudyRoutes"
      //         );
      //         return { Component: CMSPreviewWrapperComp };
      //       },
      //     },
      //   ],
      // },
    ],
  },
  //participant app routes
  {
    path: "/registry",
    element: (
      <UnAuthenticatedRoute>
        <Box
          sx={{
            height: "100vh",
            overflow: "auto",
          }}
        >
          {/* <Navbar /> */}
          <Outlet />
        </Box>
      </UnAuthenticatedRoute>
    ),
    children: [
      {
        index: true,
        element: <LandingPageV2 />,
      },
      {
        path: "login",
        element: <ParticipantLogin />,
      },
      {
        path: "register",
        element: <AccountCreation />,
      },
      {
        path: "verify-email",
        element: <EmailVerification />,
      },
    ],
  },
  {
    path: "/participant/app",
    element: <ParticipantLayout />,
    children: [
      {
        path: "core-surveys",
        element: (
          <AuthenticatedRoute allowedRoles={["participant"]}>
            <CoreSurvey />
          </AuthenticatedRoute>
        ),
      },
      {
        path: "surveys/:id/package/:surveyId",
        element: (
          <AuthenticatedRoute allowedRoles={["participant"]}>
            <Survey />
          </AuthenticatedRoute>
        ),
      },
      {
        path: "study-surveys",
        element: (
          <AuthenticatedRoute allowedRoles={["participant"]}>
            <StudySurvey />
          </AuthenticatedRoute>
        ),
      },
      {
        path: "core-consents",
        element: (
          <AuthenticatedRoute allowedRoles={["participant"]}>
            <CoreConsents />
          </AuthenticatedRoute>
        ),
        children: [
          {
            path: "sign/:consentId",
            element: (
              <AuthenticatedRoute allowedRoles={["participant"]}>
                <ConsentForm />
              </AuthenticatedRoute>
            ),
          },
        ],
      },

      {
        path: "study-consents",
        element: (
          <AuthenticatedRoute allowedRoles={["participant"]}>
            <StudyConsents />
          </AuthenticatedRoute>
        ),
        children: [
          {
            path: "sign/:consentId",
            element: (
              <AuthenticatedRoute allowedRoles={["participant"]}>
                <ConsentForm />
              </AuthenticatedRoute>
            ),
          },
        ],
      },
      {
        path: "health-records",
        element: (
          <AuthenticatedRoute allowedRoles={["participant"]}>
            <MyHealthRecords />
          </AuthenticatedRoute>
        ),
      },
    ],
  },

  // {
  //   path: "/participant",
  //   lazy: async () => {
  //     const { ParticipantLayoutComp } = await import(
  //       "./Routes/ParticipantsRoutes"
  //     );
  //     return { Component: ParticipantLayoutComp };
  //   },
  //   children: [
  //     {
  //       path: "home",
  //       lazy: async () => {
  //         const { HomeComp } = await import("./Routes/ParticipantsRoutes");
  //         return { Component: HomeComp };
  //       },
  //     },
  //     {
  //       path: "questionnaires",
  //       lazy: async () => {
  //         const { QuestionnaireComp } = await import(
  //           "./Routes/ParticipantsRoutes"
  //         );
  //         return { Component: QuestionnaireComp };
  //       },
  //     },
  //     {
  //       path: "questionnaires/:id/survey/:surveyId",
  //       lazy: async () => {
  //         const { SurveyComp } = await import("./Routes/ParticipantsRoutes");
  //         return { Component: SurveyComp };
  //       },
  //     },
  //     {
  //       path: "e-consent",
  //       children: [
  //         {
  //           index: true,
  //           lazy: async () => {
  //             const { EConsentComp } = await import(
  //               "./Routes/ParticipantsRoutes"
  //             );
  //             return { Component: EConsentComp };
  //           },
  //         },
  //       ],
  //     },
  //     {
  //       path: "appointments",
  //       lazy: async () => {
  //         const { ParticipantAppointmentsComp } = await import(
  //           "./Routes/ParticipantsRoutes"
  //         );
  //         return { Component: ParticipantAppointmentsComp };
  //       },
  //     },
  //   ],
  // },
  {
    path: "sign/:id",
    lazy: async () => {
      const { SignConsent } = await import("./Routes/ParticipantsRoutes");
      return { Component: SignConsent };
    },
  },
  {
    path: "link/participant/redirect/:type",
    element: <LinkParticipant />,
  },
  // {
  //   path: "/telehealth/:studyId/:apptId",
  //   lazy: async () => {
  //     const { OpenTokComp } = await import("./Routes/StudyRoutes");
  //     return { Component: OpenTokComp };
  //   },
  // },
  {
    path: "/screening/:id",
    element: (
      <AuthenticatedRoute allowedRoles={["participant"]}>
        <Screening />
      </AuthenticatedRoute>
    ),
  },
  // {
  //   path: "/lesson/read/:unitId",
  //   element: <WebviewWrapper />,
  // },
  {
    path: "/participant/app",
    element: <Navigate to="/home" replace />,
  },
  {
    path: "/",
    element: <Navigate to="/home" replace />,
  },
  {
    path: "*",
    element: <Navigate to="/home" replace />,
  },
]);

export default routes;
